import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Fuel, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email, reason: reason || undefined });
      setDone(true);
      toast.success(data.message || "Reset request submitted");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <div className="w-full max-w-md ft-fade-up border border-[#262626] bg-[#121212] p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#FF5A1F] flex items-center justify-center">
            <Fuel size={20} strokeWidth={2.5} className="text-black" />
          </div>
          <div>
            <p className="ft-overline">FUELTRACK</p>
            <h2 className="font-display text-2xl font-black tracking-tighter">Reset password</h2>
          </div>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="forgot-done">
            <CheckCircle2 size={40} className="text-[#10B981] mx-auto" />
            <p className="mt-4 text-white font-display text-xl">Request submitted</p>
            <p className="mt-2 text-[#A3A3A3] text-sm">
              If your account exists, an admin will review your request and set a new password. You'll be able to sign in with the new password once it's set.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-2 text-[#FF5A1F] hover:underline text-sm"
              data-testid="forgot-back-login"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form">
            <p className="text-[#A3A3A3] text-sm mb-6">
              Enter your email and (optionally) a short note. An admin will manually reset your password.
            </p>
            <label className="ft-overline block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] mb-4 transition-colors duration-200"
              data-testid="forgot-email-input"
            />
            <label className="ft-overline block mb-2">Reason (optional)</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] mb-6 transition-colors duration-200"
              placeholder="e.g. forgot password after phone reset"
              data-testid="forgot-reason-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF5A1F] text-black font-bold px-6 py-3 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="forgot-submit-button"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Submit request
            </button>
            <p className="mt-6 text-sm">
              <Link to="/login" className="text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid="forgot-back-login">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
